import type {
  NotificationEvent,
  NotificationEventInput,
} from "./notification-event";

export const NOTIFICATION_DEDUPE_WINDOW_MS = 30_000;
const MAX_RETAINED_EVENTS = 100;

function normalized(value: string | undefined) {
  return (value ?? "")
    .normalize("NFKC")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase();
}

export function notificationFingerprint(input: NotificationEventInput) {
  return [
    normalized(input.source),
    normalized(input.title),
    normalized(input.body),
    normalized(input.action?.label),
  ].join("|");
}

function resolveExpiry(input: NotificationEventInput, now: number) {
  if (input.persistence === "acknowledgement") return null;
  if (input.durationMs === null || input.durationMs === 0) return null;
  return now + (input.durationMs ?? 4_000);
}

function createEvent(
  input: NotificationEventInput,
  now: number,
  id: string,
): NotificationEvent {
  const createdAt = input.createdAt ?? now;
  return {
    id: input.id ?? id,
    source: input.source,
    kind: input.kind,
    severity: input.severity ?? "info",
    title: input.title.trim(),
    body: input.body?.trim() || undefined,
    dedupeKey: input.dedupeKey,
    fingerprint: notificationFingerprint(input),
    createdAt,
    updatedAt: now,
    expiresAt: resolveExpiry(input, now),
    persistence: input.persistence ?? "transient",
    action: input.action,
    secondaryAction: input.secondaryAction,
    nativeDeliveryEligible: input.nativeDeliveryEligible ?? false,
    readAt: null,
    dismissedAt: null,
    repeatCount: 1,
    notificationType: input.notificationType,
    legacyVariant: input.legacyVariant,
    legacyPosition: input.legacyPosition,
    leadingGlyph: input.leadingGlyph,
    riskScore: input.riskScore,
    persistentHistoryId: input.persistentHistoryId,
  };
}

function matchesForDedupe(
  event: NotificationEvent,
  input: NotificationEventInput,
  fingerprint: string,
  now: number,
) {
  if (event.dismissedAt !== null) return false;
  if (input.dedupeKey) return event.dedupeKey === input.dedupeKey;
  return (
    event.fingerprint === fingerprint &&
    now - event.updatedAt <= NOTIFICATION_DEDUPE_WINDOW_MS
  );
}

export function upsertNotificationEvent(
  current: NotificationEvent[],
  input: NotificationEventInput,
  now: number,
  nextId: () => string,
) {
  const fingerprint = notificationFingerprint(input);
  const existing = current.find((event) =>
    matchesForDedupe(event, input, fingerprint, now),
  );

  if (existing) {
    const refreshed: NotificationEvent = {
      ...existing,
      ...input,
      id: existing.id,
      fingerprint,
      body: input.body?.trim() || existing.body,
      createdAt: existing.createdAt,
      updatedAt: now,
      expiresAt: resolveExpiry(input, now),
      dismissedAt: null,
      repeatCount: existing.repeatCount + 1,
    };
    return {
      id: existing.id,
      events: [
        refreshed,
        ...current.filter((event) => event.id !== existing.id),
      ].slice(0, MAX_RETAINED_EVENTS),
    };
  }

  const event = createEvent(input, now, nextId());
  return {
    id: event.id,
    events: [event, ...current].slice(0, MAX_RETAINED_EVENTS),
  };
}

export function dismissNotificationEvent(
  current: NotificationEvent[],
  id: string,
  now: number,
) {
  return current.map((event) =>
    event.id === id ? { ...event, dismissedAt: now } : event,
  );
}

export function dismissTransientNotificationEvents(
  current: NotificationEvent[],
  now: number,
) {
  return current.map((event) =>
    event.persistence === "transient" && event.dismissedAt === null
      ? { ...event, dismissedAt: now }
      : event,
  );
}

export function visibleNotificationEvents(current: NotificationEvent[]) {
  return current
    .filter((event) => event.dismissedAt === null)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}
